import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TrendingUp, Users, FileSignature, Receipt, AlertTriangle, Award } from "lucide-react";

const MESI = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const TYPE_LABEL = {
  holiday: "Ferie",
  permission: "Permessi",
  rol: "ROL",
  law_104: "Legge 104",
  parental_leave: "Congedo parentale",
  medical_visit: "Visita medica",
  union: "Sindacale",
  illness: "Malattia",
  other: "Altro",
};

export default function StatsPage() {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(6);

  useEffect(() => {
    api.get(`/stats/advanced?months=${months}`).then((r) => setData(r.data));
  }, [months]);

  if (!data) return <div className="p-12">Caricamento…</div>;
  const maxAtt = Math.max(...data.monthly_attendance.map((m) => (m.in || 0) + (m.out || 0)), 1);

  return (
    <div className="p-4 md:p-12" data-testid="stats-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Insights</div>
          <h1 className="font-display text-4xl">Statistiche avanzate</h1>
          <p className="text-[#555] mt-1 text-sm">Trend mensili · top dipendenti · anomalie · spese · ferie.</p>
        </div>
        <select value={months} onChange={(e)=>setMonths(Number(e.target.value))} data-testid="stats-months" className="rounded-xl border border-[#EDE5D8] px-3 h-11">
          <option value={3}>Ultimi 3 mesi</option>
          <option value={6}>Ultimi 6 mesi</option>
          <option value={12}>Ultimi 12 mesi</option>
          <option value={24}>Ultimi 24 mesi</option>
        </select>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPI label="Dipendenti attivi" value={data.headcount} icon={Users} color="orange"/>
        <KPI label="Documenti firmati 30gg" value={data.docs_signed_30d} icon={FileSignature} color="black"/>
        <KPI label="Rimborsi approvati" value={`€ ${data.expenses.approved_total}`} icon={Receipt} color="black"/>
        <KPI label="Anomalie totali" value={data.anomalies_total} icon={AlertTriangle} color="red"/>
      </div>

      {/* Chart timbrature */}
      <div className="card-smart p-6 mb-6">
        <h2 className="font-display text-xl mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#FF7A29]"/> Timbrature mensili</h2>
        <div className="space-y-3">
          {data.monthly_attendance.map((m) => {
            const total = (m.in || 0) + (m.out || 0);
            const pct = (total / maxAtt) * 100;
            const [y, mo] = m.month.split("-");
            return (
              <div key={m.month} data-testid={`month-${m.month}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold">{MESI[parseInt(mo, 10) - 1]} {y}</span>
                  <span className="text-[#555]">
                    <span className="text-green-700">{m.in} in</span> · <span className="text-amber-700">{m.out} out</span> · <span className="text-orange-700">{m.lunch} pause</span>
                    {m.anomalies > 0 && <span className="text-red-600 ml-2">⚠ {m.anomalies} anomalie</span>}
                  </span>
                </div>
                <div className="h-2 bg-[#FFF0E3] rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF7A29] transition-all" style={{ width: `${pct}%` }}/>
                </div>
              </div>
            );
          })}
          {data.monthly_attendance.length === 0 && <div className="text-sm text-[#757575] text-center py-6">Nessun dato.</div>}
        </div>
      </div>

      {/* Top employees + Ferie */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-smart p-6">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Award className="w-5 h-5 text-[#FF7A29]"/> Top 5 dipendenti per timbrature</h2>
          {data.top_employees.length === 0 ? <div className="text-sm text-[#757575]">Nessun dato.</div> : (
            <ul className="space-y-2">
              {data.top_employees.map((t, i) => (
                <li key={i} className="flex items-center justify-between bg-[#FFFAF5] rounded-xl px-3 py-2 border border-[#EDE5D8]">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#FF7A29] text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      {t.anomalies > 0 && <div className="text-[10px] text-red-600">⚠ {t.anomalies} anomalie</div>}
                    </div>
                  </div>
                  <div className="font-mono font-bold text-[#FF7A29]">{t.count}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-smart p-6">
          <h2 className="font-display text-xl mb-4">Ferie e permessi</h2>
          {Object.keys(data.leaves_by_type).length === 0 ? <div className="text-sm text-[#757575]">Nessuna richiesta.</div> : (
            <div className="space-y-3">
              {Object.entries(data.leaves_by_type).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-sm">{TYPE_LABEL[k] || k}</span>
                  <span className="font-mono font-bold text-[#FF7A29]">{v}</span>
                </div>
              ))}
              <div className="border-t border-[#EDE5D8] pt-3 mt-3 flex items-center justify-between">
                <span className="text-sm font-semibold">Rimborsi pending</span>
                <span className="font-mono font-bold text-amber-700">{data.expenses.pending_count}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  const colorMap = {
    orange: "bg-[#FFF0E3] text-[#FF7A29]",
    black: "bg-black text-[#FF7A29]",
    red: "bg-red-100 text-red-600",
  };
  return (
    <div className="card-smart p-5">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colorMap[color]}`}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="text-2xl font-display font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-1">{label}</div>
    </div>
  );
}
